import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getGetAnalyticsSummaryQueryKey,
  getGetAppointmentsByStatusQueryKey,
  getGetRevenueStatsQueryKey,
  getGetTrafficByDayQueryKey,
  useGetAnalyticsSummary,
  useGetAppointmentsByStatus,
  useGetRevenueStats,
  useGetTrafficByDay,
} from '@/hooks/use-api';
import {
  Activity,
  AlertCircle,
  Calendar,
  DollarSign,
  Eye,
  Users,
} from 'lucide-react';
import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

export default function AdminDashboard() {
  // Fetch all data with error handling
  const {
    data: summary,
    isLoading: loadingSummary,
    isError: errorSummary,
  } = useGetAnalyticsSummary({
    query: { queryKey: getGetAnalyticsSummaryQueryKey() },
  });

  const {
    data: traffic,
    isLoading: loadingTraffic,
    isError: errorTraffic,
  } = useGetTrafficByDay({
    query: { queryKey: getGetTrafficByDayQueryKey() },
  });

  const {
    data: apptStats,
    isLoading: loadingApptStats,
    isError: errorApptStats,
  } = useGetAppointmentsByStatus({
    query: { queryKey: getGetAppointmentsByStatusQueryKey() },
  });

  const {
    data: revenueData,
    isLoading: loadingRevenue,
    isError: errorRevenue,
  } = useGetRevenueStats({
    query: { queryKey: getGetRevenueStatsQueryKey() },
  });

  // Safely handle revenue data - ensure it's always an array
  const revenue = React.useMemo(() => {
    try {
      if (!revenueData) return [];
      if (Array.isArray(revenueData)) return revenueData;
      // If it's an object with results property
      if (typeof revenueData === 'object' && 'results' in revenueData) {
        return Array.isArray((revenueData as any).results)
          ? (revenueData as any).results
          : [];
      }
      return [];
    } catch (e) {
      console.error('Error processing revenue data:', e);
      return [];
    }
  }, [revenueData]);

  const COLORS = ['#0ea5e9', '#0a2540', '#0369a1', '#94a3b8'];

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="px-3 py-1 bg-red-500 text-white rounded-md font-rajdhani font-bold text-sm tracking-wider">
            ADMIN
          </div>
        </div>
        <h1 className="font-bebas text-4xl tracking-wide mb-2">
          SYSTEM OVERVIEW
        </h1>
        <p className="font-sans text-slate-500">
          Key metrics and performance indicators for administrators.
        </p>
      </div>

      {/* Show error alert if any API fails */}
      {(errorSummary || errorTraffic || errorApptStats || errorRevenue) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Data Loading Error</AlertTitle>
          <AlertDescription>
            Some dashboard data failed to load. The page will display available
            information. Try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="TOTAL REVENUE"
          value={
            summary?.totalRevenue != null
              ? `$${summary.totalRevenue.toLocaleString()}`
              : null
          }
          icon={<DollarSign className="w-5 h-5" />}
          loading={loadingSummary}
          error={errorSummary}
        />
        <StatCard
          title="APPOINTMENTS"
          value={summary?.totalAppointments ?? null}
          icon={<Calendar className="w-5 h-5" />}
          loading={loadingSummary}
          error={errorSummary}
        />
        <StatCard
          title="USERS"
          value={summary?.totalUsers ?? null}
          icon={<Users className="w-5 h-5" />}
          loading={loadingSummary}
          error={errorSummary}
        />
        <StatCard
          title="PAGE VIEWS"
          value={summary?.totalPageViews ?? null}
          icon={<Eye className="w-5 h-5" />}
          loading={loadingSummary}
          error={errorSummary}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Traffic Chart */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" /> SITE TRAFFIC (LAST
              30 DAYS)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingTraffic ? (
              <Skeleton className="w-full h-[300px]" />
            ) : errorTraffic ? (
              <div className="h-[300px] flex items-center justify-center">
                <Alert variant="destructive" className="max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load traffic data
                  </AlertDescription>
                </Alert>
              </div>
            ) : traffic && Array.isArray(traffic) && traffic.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={traffic}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorViews"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#0ea5e9"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#0ea5e9"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#334155"
                      opacity={0.2}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={val =>
                        new Date(val).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        borderColor: '#1e293b',
                        color: '#f8fafc',
                        borderRadius: '8px',
                      }}
                      itemStyle={{ color: '#0ea5e9' }}
                      labelFormatter={label =>
                        new Date(label).toLocaleDateString(undefined, {
                          month: 'long',
                          day: 'numeric',
                        })
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="views"
                      stroke="#0ea5e9"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorViews)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500 font-sans">
                No traffic data available.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appointment Status Pie */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-rajdhani font-bold tracking-wider">
              APPOINTMENT STATUS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingApptStats ? (
              <Skeleton className="w-full h-[300px]" />
            ) : errorApptStats ? (
              <div className="h-[300px] flex items-center justify-center">
                <Alert variant="destructive" className="max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load appointment data
                  </AlertDescription>
                </Alert>
              </div>
            ) : apptStats &&
              Array.isArray(apptStats) &&
              apptStats.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={apptStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="status"
                    >
                      {apptStats.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        borderColor: '#1e293b',
                        color: '#f8fafc',
                        borderRadius: '8px',
                        textTransform: 'capitalize',
                      }}
                    />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{
                        fontSize: '12px',
                        fontFamily: 'var(--app-font-rajdhani)',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500 font-sans">
                No appointment data available.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Bar Chart */}
        <Card className="lg:col-span-3 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-rajdhani font-bold tracking-wider">
              REVENUE BY MONTH
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRevenue ? (
              <Skeleton className="w-full h-[350px]" />
            ) : errorRevenue ? (
              <div className="h-[350px] flex items-center justify-center">
                <Alert variant="destructive" className="max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load revenue data
                  </AlertDescription>
                </Alert>
              </div>
            ) : revenue && revenue.length > 0 ? (
              <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={revenue}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#334155"
                      opacity={0.2}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      tickFormatter={val => `$${val}`}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        borderColor: '#1e293b',
                        color: '#f8fafc',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'rgba(14, 165, 233, 0.1)' }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: '12px',
                        fontFamily: 'var(--app-font-rajdhani)',
                        fontWeight: 'bold',
                        paddingTop: '10px',
                      }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      name="Revenue ($)"
                      fill="#0ea5e9"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="appointments"
                      name="Appointments"
                      fill="#0a2540"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-slate-500 font-sans">
                No revenue data available.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  loading,
  error,
}: {
  title: string;
  value: string | number | null;
  icon: React.ReactNode;
  loading: boolean;
  error?: boolean;
}) {
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-rajdhani font-bold tracking-wider text-slate-500 text-sm mb-1">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-10 w-24" />
            ) : error ? (
              <h3 className="font-bebas text-2xl tracking-wide text-red-500">
                ERROR
              </h3>
            ) : (
              <h3 className="font-bebas text-4xl tracking-wide">
                {value ?? '0'}
              </h3>
            )}
          </div>
          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-primary">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
